import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2, EyeOff, Eye } from 'lucide-react';
import Overlay from './PlannerOverlay';
import { BUILTIN_FEATURES } from './plannerCategories';
import { getPlannerCategories, createPlannerCategory, updatePlannerCategory, deletePlannerCategory } from '../../api';

// Category manager for the week planner: add, rename, recolour, change the default title, hide or
// delete. Built-in categories carry special fields, so they're hidden instead of deleted (and can
// be restored); custom categories are deleted, after moving any blocks that still use them.
// The text colour on a category is chosen by the server for readability — the preview here uses
// the same rule.

function previewTextColor(bg) {
  const lin = c => { const v = c / 255; return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4; };
  const lum = hex => { const n = parseInt(hex.slice(1), 16); return 0.2126 * lin((n >> 16) & 255) + 0.7152 * lin((n >> 8) & 255) + 0.0722 * lin(n & 255); };
  const L = lum(bg);
  return 1.05 / (L + 0.05) >= (L + 0.05) / (lum('#15171a') + 0.05) ? '#ffffff' : '#15171a';
}

function Chip({ label, bg }) {
  return (
    <span style={{ display: 'inline-block', padding: '3px 8px', borderRadius: 5, fontSize: 12, fontWeight: 700, background: bg, color: previewTextColor(bg), maxWidth: 170, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {label || 'New category'}
    </span>
  );
}

export default function CategoriesDialog({ token, onClose, onChanged }) {
  const [list, setList] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState({ label: '', bg: '#0ea5e9', default_title: '' });

  const load = useCallback(async () => {
    try { setList(await getPlannerCategories(token)); setError(null); } catch (e) { setError(e.message); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const run = async fn => {
    setBusy(true);
    try { await fn(); setError(null); return true; } catch (e) { setError(e.message); return false; } finally {
      setBusy(false);
      await load();
      onChanged();
    }
  };

  const active = (list || []).filter(c => !c.archived_at);
  const hidden = (list || []).filter(c => c.archived_at);

  return (
    <Overlay title="Block categories" onClose={onClose} width={560}>
      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
        Changes apply to every week straight away (names and colours only — no block changes). Built-in categories have
        special fields, so they can be hidden but not deleted.
      </div>
      {error && <div role="alert" style={{ fontSize: 13, color: 'var(--pl-err)' }}>{error}</div>}
      {!list && !error && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading…</div>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {active.map(c => (
          <CategoryRow key={c.key} cat={c} others={active.filter(o => o.key !== c.key)} busy={busy}
            onSave={patch => run(() => updatePlannerCategory(token, c.key, patch))}
            onHide={() => run(() => updatePlannerCategory(token, c.key, { archived: true }))}
            onDelete={moveTo => run(() => deletePlannerCategory(token, c.key, moveTo))} />
        ))}
      </div>

      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>New category</span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label className="pl-label">Colour<input type="color" value={creating.bg} onChange={e => setCreating(f => ({ ...f, bg: e.target.value }))} style={{ width: 44, height: 34, padding: 0, border: 'none', background: 'none' }} /></label>
          <label className="pl-label" style={{ flex: 1, minWidth: 140 }}>Name<input className="pl-input" value={creating.label} maxLength={40} onChange={e => setCreating(f => ({ ...f, label: e.target.value }))} placeholder="e.g. Pool session" /></label>
          <label className="pl-label" style={{ flex: 1, minWidth: 140 }}>Default block title<input className="pl-input" value={creating.default_title} onChange={e => setCreating(f => ({ ...f, default_title: e.target.value }))} placeholder="optional" /></label>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <Chip label={creating.label} bg={creating.bg} />
          <button className="pl-btn pl-btn--primary" disabled={busy || !creating.label.trim()}
            onClick={async () => {
              if (await run(() => createPlannerCategory(token, creating))) setCreating({ label: '', bg: '#0ea5e9', default_title: '' });
            }}><Plus size={14} /> Add category</button>
        </div>
      </div>

      {hidden.length > 0 && (
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>Hidden built-in categories</span>
          {hidden.map(c => (
            <div key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
              <Chip label={c.label} bg={c.bg} />
              <span style={{ flex: 1, color: 'var(--text-muted)' }}>{c.block_count} block{c.block_count === 1 ? '' : 's'} still use it</span>
              <button className="pl-btn" disabled={busy} onClick={() => run(() => updatePlannerCategory(token, c.key, { archived: false }))}><Eye size={14} /> Restore</button>
            </div>
          ))}
        </div>
      )}
    </Overlay>
  );
}

function CategoryRow({ cat, others, busy, onSave, onHide, onDelete }) {
  const [form, setForm] = useState({ label: cat.label, bg: cat.bg, default_title: cat.default_title });
  const [deleting, setDeleting] = useState(false);
  const [moveTo, setMoveTo] = useState(others[0]?.key || '');
  useEffect(() => { setForm({ label: cat.label, bg: cat.bg, default_title: cat.default_title }); }, [cat.label, cat.bg, cat.default_title]);
  const dirty = form.label !== cat.label || form.bg !== cat.bg || form.default_title !== cat.default_title;
  const feature = cat.builtin ? BUILTIN_FEATURES[cat.key] : null;

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <label className="pl-label">Colour<input type="color" value={form.bg} aria-label={`Colour of ${cat.label}`} onChange={e => setForm(f => ({ ...f, bg: e.target.value }))} style={{ width: 44, height: 34, padding: 0, border: 'none', background: 'none' }} /></label>
        <label className="pl-label" style={{ flex: 1, minWidth: 140 }}>Name<input className="pl-input" value={form.label} maxLength={40} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} /></label>
        <label className="pl-label" style={{ flex: 1, minWidth: 140 }}>Default block title<input className="pl-input" value={form.default_title} onChange={e => setForm(f => ({ ...f, default_title: e.target.value }))} /></label>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 12 }}>
        <Chip label={form.label} bg={form.bg} />
        <span style={{ color: 'var(--text-muted)', flex: 1, minWidth: 160 }}>
          {cat.builtin ? 'Built-in' : 'Custom'} · {cat.block_count} block{cat.block_count === 1 ? '' : 's'}{feature ? ` · ${feature}` : ''}
        </span>
        {dirty && <button className="pl-btn pl-btn--primary" disabled={busy || !form.label.trim()} onClick={() => onSave(form)}>Save</button>}
        {cat.builtin
          ? <button className="pl-btn" disabled={busy} onClick={onHide} title="Hide from the category list; existing blocks keep it"><EyeOff size={14} /> Hide</button>
          : <button className="pl-btn" disabled={busy} onClick={() => (cat.block_count ? setDeleting(d => !d) : window.confirm(`Delete "${cat.label}"?`) && onDelete(null))}><Trash2 size={14} /> Delete</button>}
      </div>
      {deleting && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 13, background: 'var(--pl-warn-bg)', border: '1px solid var(--pl-warn-border)', borderRadius: 8, padding: 8 }}>
          Move its {cat.block_count} block{cat.block_count === 1 ? '' : 's'} to
          <select className="pl-input" value={moveTo} onChange={e => setMoveTo(e.target.value)} aria-label="Move blocks to">
            {others.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
          <button className="pl-btn pl-btn--primary" disabled={busy || !moveTo} onClick={() => onDelete(moveTo)}>Move and delete</button>
          <button className="pl-btn" onClick={() => setDeleting(false)}>Cancel</button>
        </div>
      )}
    </div>
  );
}
